import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * UI Text Registry — loads all admin-editable headings/labels once at boot,
 * exposes `t(key, defaultText)` which returns the customized string if the
 * Super Admin has set one, otherwise the code default. Re-fetches on every
 * mount so admin edits show up on next page navigation.
 */
const UiTextContext = createContext({
  loaded: false,
  entries: {},
  t: (_key, fallback) => fallback,
  reload: async () => {},
});

export function UiTextProvider({ children }) {
  const [entries, setEntries] = useState({});
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/ui-text");
      const map = {};
      for (const row of data) map[row.key] = row.effective_text;
      setEntries(map);
    } catch {
      /* silently ignore — anonymous or 401 pages just get code defaults */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => ({
    loaded,
    entries,
    t: (key, fallback = "") => entries[key] ?? fallback,
    reload,
  }), [entries, loaded, reload]);

  return <UiTextContext.Provider value={value}>{children}</UiTextContext.Provider>;
}

export function useUiText() {
  return useContext(UiTextContext);
}
